import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import styles from './LandingPage.module.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const rootRef = useRef(null);

  // Gentle scroll-reveal: fade + rise elements as they enter the viewport.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const els = root.querySelectorAll('[data-reveal]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => el.setAttribute('data-revealed', 'true'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-revealed', 'true');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <PublicLayout className="lp-fullbleed">
      <div className={styles.landingPage} ref={rootRef}>

        {/* Hero */}
        <section className={styles.lpHero}>
          <div className={styles.lpHeroInner}>
            <h2 className={styles.lpHeroBrand} data-reveal>Vates Domus AI</h2>
            <p className={styles.lpHeroLabel} data-reveal style={{ transitionDelay: '60ms' }}>
              <span className={styles.lpHeroLabelDot} aria-hidden="true" />
              Real Estate Intelligence Platform
            </p>
            <h1 className={styles.lpHeroHeading} data-reveal style={{ transitionDelay: '120ms' }}>
              <span className={styles.lpHeroAccent}>Chat with MLS</span>, Virtually Stage<br />
              &amp; Generate AI CMAs
            </h1>
            <h2 className={styles.lpHeroTagline} data-reveal style={{ transitionDelay: '180ms' }}>FOR REAL ESTATE PROFESSIONALS</h2>
            <p className={styles.lpHeroSub} data-reveal style={{ transitionDelay: '240ms' }}>
              Vates Domus AI gives real estate professionals real-time MLS search, AI-powered property conversations, Comparative Market Analysis reports where you choose the comparables, AI virtual staging, and cinematic listing videos.
            </p>
            <div className={styles.lpHeroActions} data-reveal style={{ transitionDelay: '300ms' }}>
              <button className={`${styles.lpBtn} ${styles.lpBtnPrimary}`} onClick={() => navigate('/pricing')}>
                Get Started <span aria-hidden="true">&rarr;</span>
              </button>
              <button
                className={`${styles.lpBtn} ${styles.lpBtnGhost}`}
                onClick={() => {
                  document.querySelector(`.${styles.lpDemo}`)?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Watch Demo
              </button>
            </div>
            <div className={styles.lpHeroCompliance} data-reveal style={{ transitionDelay: '360ms' }}>
              <p className={styles.lpHeroComplianceTitle}>Compliant by Design, Grounded in Truth.</p>
              <p className={styles.lpHeroComplianceBody}>
                At Vates Domus AI, we do not scrape MLS data. We respect the rules, bylaws, and intellectual property of the National Association of Realtors (NAR) and local boards. Our platform interfaces strictly through approved, official real estate data feeds. Your data—and your MLS standing—are 100% secure with us.
              </p>
            </div>
          </div>
        </section>

        {/* Testimonials Video */}
        <section className={styles.lpTestimonials}>
          <div className={styles.lpSectionInner}>
            <h2 className={styles.lpSectionTitle} data-reveal>Trusted by Real Estate Professionals</h2>
            <p className={styles.lpSectionSub} data-reveal style={{ transitionDelay: '80ms' }}>Hear what agents and brokers are saying about Vates Domus AI</p>
            <div className={styles.lpTestimonialRow} data-reveal style={{ transitionDelay: '140ms' }}>
              <div className={styles.lpReelWrap}>
                <video
                  src="/RealProducers-VatesDomus.mov"
                  controls
                  playsInline
                  preload="metadata"
                />
              </div>
              <div className={styles.lpQuotes}>
                <div className={styles.lpQuoteCol}>
                  <blockquote className={styles.lpQuote}>
                    <p>
                      “This is the perfect weapon for real estate agents. It produces a CMA in minutes without compromising the skill of the agent, agents are able to choose their comparable and it generates a modern comparative analysis. The product also helps agents better market their listing using the chat feature. Agents are able to ask property specific questions using AI, even based on the neighborhood that it&apos;s being compared to.”
                    </p>
                    <footer>Nema B</footer>
                  </blockquote>
                  <blockquote className={styles.lpQuote}>
                    <p>
                      “All I ask for is an unfair advantage, and this product provides that”
                    </p>
                    <footer>Jim B</footer>
                  </blockquote>
                </div>
                <div className={styles.lpQuoteCol}>
                  <blockquote className={styles.lpQuote}>
                    <p>
                      “It makes it so much easier for me to explain to my clients… and they&apos;re reading the report they understand why I pulled the comps that I did compared to the subject property”
                    </p>
                    <footer>Candice T</footer>
                  </blockquote>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scrolling Feature Ribbon */}
        <div className={styles.lpRibbon}>
          <div className={styles.lpRibbonTrack}>
            {[
              'Search by Zip Code',
              'Interactive Map Search',
              'Draw Custom Polygons',
              'Chat with Properties',
              'AI CMA Generation',
              'AI Virtual Staging',
              'Video Montage',
              'Print-Ready Reports',
              'Dozens of Markets',
              'Real-Time Data',
            ].map((t, i) => (
              <span className={styles.lpRibbonItem} key={i}>{t}</span>
            ))}
            {/* duplicate for seamless loop */}
            {[
              'Search by Zip Code',
              'Interactive Map Search',
              'Draw Custom Polygons',
              'Chat with Properties',
              'AI CMA Generation',
              'AI Virtual Staging',
              'Video Montage',
              'Print-Ready Reports',
              'Dozens of Markets',
              'Real-Time Data',
            ].map((t, i) => (
              <span className={styles.lpRibbonItem} key={`d-${i}`}>{t}</span>
            ))}
          </div>
        </div>

        {/* Demo Video */}
        <section className={styles.lpDemo}>
          <div className={styles.lpSectionInner}>
            <h2 className={styles.lpSectionTitle} data-reveal>See It in Action</h2>
            <p className={styles.lpSectionSub} data-reveal style={{ transitionDelay: '80ms' }}>Watch a walkthrough of the platform</p>
            <div className={styles.lpVideoWrap} data-reveal style={{ transitionDelay: '140ms' }}>
              <iframe
                src="https://www.youtube.com/embed/1QzQ1UnzOpg?autoplay=1&mute=1&loop=1&playlist=1QzQ1UnzOpg&vq=hd1080"
                title="Vates Domus AI Demo"
                width="1920"
                height="1080"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        {/* Features */}
        <section className={styles.lpFeatures}>
          <div className={styles.lpSectionInner}>
            <h2 className={styles.lpSectionTitle} data-reveal>Everything You Need</h2>
            <p className={styles.lpSectionSub} data-reveal style={{ transitionDelay: '80ms' }}>One platform for search, analysis, reporting, and listing visuals</p>

            <div className={styles.lpFeaturesGrid}>
              {/* MLS GPT */}
              <div className={styles.lpCard} data-reveal>
                <div className={styles.lpCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                </div>
                <h3>MLS GPT</h3>
                <p>Search the MLS by zip code, AI search, or interactive map, then chat with your selected properties: summarize listings, draft buyer emails, build comparison tables, and explore school districts, area events, and public transit.</p>
                <div className={styles.lpCardTags}>
                  <span>Zip Code Search</span><span>AI Search</span><span>Map Search</span><span>Summarize Properties</span><span>Draft Buyer Emails</span><span>Comparison Tables</span><span>School Districts</span><span>Area Insights</span>
                </div>
              </div>

              {/* Map Search */}
              <div className={styles.lpCard} data-reveal style={{ transitionDelay: '90ms' }}>
                <div className={styles.lpCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                </div>
                <h3>Search by Map</h3>
                <p>Search by address, zip code, or draw a custom polygon right on the map. View active, pending, sold, and closed listings, switch to satellite view, and click any marker to open the full property card.</p>
                <div className={styles.lpCardTags}>
                  <span>Address &amp; Zip Search</span><span>Draw Polygons</span><span>Active / Pending / Sold</span><span>Satellite View</span><span>Interactive Markers</span>
                </div>
              </div>

              {/* CMA */}
              <div className={styles.lpCard} data-reveal style={{ transitionDelay: '180ms' }}>
                <div className={styles.lpCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                </div>
                <h3>AI CMA Generation</h3>
                <p>Generate professional Comparative Market Analysis reports from MLS data or from scratch. You choose the active, pending, and sold comps, then visualize pricing with scatter plots, run mortgage analysis, and export clean, print-ready reports.</p>
                <div className={styles.lpCardTags}>
                  <span>You Choose the Comps</span><span>From MLS</span><span>From Scratch</span><span>Scatter Plots</span><span>Mortgage Analysis</span><span>Print-Ready Reports</span>
                </div>
              </div>

              {/* Tax Record Search */}
              <div className={styles.lpCard} data-reveal style={{ transitionDelay: '270ms' }}>
                <div className={styles.lpCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><line x1="8" y1="9" x2="10" y2="9"/></svg>
                </div>
                <h3>Tax Record Search</h3>
                <p>Look up property tax records by address. Access assessed values, ownership details, tax history, and property characteristics to inform your pricing and analysis.</p>
                <div className={styles.lpCardTags}>
                  <span>By Address</span><span>Assessed Values</span><span>Ownership Data</span><span>Tax History</span><span>Property Details</span>
                </div>
              </div>

              {/* Virtual Staging */}
              <div className={styles.lpCard} data-reveal style={{ transitionDelay: '360ms' }}>
                <div className={styles.lpCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                </div>
                <h3>AI Virtual Staging</h3>
                <p>Upload a listing photo and stage empty rooms with AI. Refine the last result, attach extra photos as style samples, and download images with a built-in virtually-staged disclaimer for MLS-safe marketing.</p>
                <div className={styles.lpCardTags}>
                  <span>AI Staging</span><span>Style Samples</span><span>Iterative Edits</span><span>MLS Disclaimer Banner</span>
                </div>
              </div>

              {/* Video Montage */}
              <div className={styles.lpCard} data-reveal style={{ transitionDelay: '450ms' }}>
                <div className={styles.lpCardIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
                </div>
                <h3>Video Montage</h3>
                <p>Turn listing photos into a cinematic slideshow with pan and zoom, crossfades, title overlays, optional music, and your company logo. No AI — motion is generated from your own photos for faithful, royalty-free listing videos.</p>
                <div className={styles.lpCardTags}>
                  <span>Up to 15 Photos</span><span>Title Overlay</span><span>Music Tracks</span><span>Company Logo</span><span>No AI</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={styles.lpCta}>
          <div className={`${styles.lpSectionInner} ${styles.lpCtaInner}`}>
            <p className={styles.lpCtaLabel} data-reveal>For Real Estate Professionals</p>
            <h2 className={styles.lpCtaHeading} data-reveal style={{ transitionDelay: '80ms' }}>Ready to Work Smarter?</h2>
            <p className={styles.lpCtaSub} data-reveal style={{ transitionDelay: '140ms' }}>Join agents and brokers using Vates Domus AI to search, analyze, and report faster than ever.</p>
            <div className={styles.lpHeroActions} data-reveal style={{ transitionDelay: '200ms' }}>
              <button className={`${styles.lpBtn} ${styles.lpBtnPrimary}`} onClick={() => navigate('/pricing')}>
                View Pricing <span aria-hidden="true">&rarr;</span>
              </button>
              <button className={`${styles.lpBtn} ${styles.lpBtnGhost}`} onClick={() => navigate('/faq')}>
                Learn More
              </button>
            </div>
            <p className={styles.lpCtaTrust}>Realtor / Broker ID Required &middot; Search Properties Included &middot; AI Credits for CMA Generation</p>
          </div>
        </section>

      </div>
    </PublicLayout>
  );
};

export default LandingPage;
